import React, { useRef } from "react";
import { FixedSizeList as List } from "react-window";

import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";
import {
    Delete,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Input,
} from "@formant/ui-kit";
import { Icon } from "@formant/ui/component/common/Icon";

import { OverflowActionButton } from "./F3.HomeTabs.styles";
import { getIconFromLayoutType, getIconName } from "./F3.TabIcon";

interface IF3OverflowMenuProps {
    value: string | undefined;
    items: {
        id?: string;
        name: string;
        layoutType?: LayoutType | null;
    }[];
    onChangeViewId?: (id: Uuid) => void;
}

export function F3OverflowMenu(props: IF3OverflowMenuProps) {
    const { items, onChangeViewId, value } = props;
    const [search, setSearch] = React.useState("");
    const dropdownMenuRef = useRef<HTMLDivElement>(null);
    const [contentWidth, setContentWidth] = React.useState(200);

    const filteredOptions = React.useMemo(
        () =>
            search.length > 0
                ? items.filter(option =>
                      option.name.toLowerCase().includes(search.toLowerCase()),
                  )
                : items,
        [items, search],
    );

    React.useEffect(() => {
        const maxLength = Math.max(
            ...filteredOptions.map(option => option.name.length),
        );

        const calculatedWidth = Math.max(
            200,
            Math.min(620, maxLength * 8 + 60),
        );
        setContentWidth(calculatedWidth);
    }, [filteredOptions]);

    const handleSelect = (newValue: string) => {
        onChangeViewId?.(newValue);
    };

    const ViewItem = React.memo(({ index, style, data }: any) => {
        const option = data.filteredOptions[index];
        const isSelected = option.id === data.value;
        return (
            <DropdownMenuItem
                key={option.id}
                style={{
                    ...style,
                    width: "calc(100% - 20px)",
                    marginLeft: "10px",
                    marginRight: "10px",
                }}
                onSelect={() => handleSelect(option.id ?? "")}
                className={`text-formant-color-text-secondary flex items-center justify-between min-h-[40px] rounded-md ${
                    isSelected ? "bg-formant-color-background-active-2" : ""
                }`}
            >
                <div className="flex items-center">
                    <Icon
                        size="18"
                        className="mr-2"
                        name={getIconName(
                            getIconFromLayoutType(
                                option.layoutType ?? "observe",
                            ),
                        )}
                    />
                    <span className="truncate max-w-full overflow-hidden">
                        {option.name}
                    </span>
                </div>

                {isSelected && (
                    <Icon
                        size="18"
                        name="check"
                        className="ml-2 text-formant-color-text-primary"
                    />
                )}
            </DropdownMenuItem>
        );
    });

    const itemHeight = 40;

    let height = window.innerHeight * 0.8;
    const heightByOptions = filteredOptions.length * itemHeight;
    if (heightByOptions < height) {
        height = heightByOptions;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <OverflowActionButton>
                    <Icon
                        name="chevron-down"
                        data-testid="views-chevron-down"
                    />
                </OverflowActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                ref={dropdownMenuRef}
                style={{ maxHeight: "calc(95vh - 75px)", width: contentWidth }}
                className="overflow-hidden p-0 flex flex-col gap-2"
            >
                <div className="bg-formant-color-background-primary flex items-center border-b h-14 pl-2.5">
                    <div className="relative flex w-full items-center">
                        <Input
                            placeholder="Search views"
                            onKeyDown={e => e.stopPropagation()}
                            className="flex w-full !border-b-0 bg-transparent py-3 text-sm outline-none placeholder:text-formant-color-text-secondary disabled:cursor-not-allowed disabled:opacity-50"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <Delete
                            className={`absolute top-1/2 right-2 -translate-y-1/2 transition-opacity duration-300 text-formant-color-text-secondary ${
                                !search
                                    ? "opacity-0 pointer-events-none"
                                    : "opacity-100"
                            }`}
                            onClick={() => setSearch("")}
                            type="button"
                            size={18}
                        />
                    </div>
                </div>

                <List
                    height={height}
                    itemCount={filteredOptions.length}
                    itemSize={itemHeight}
                    width={contentWidth}
                    itemData={{ filteredOptions, value }}
                >
                    {ViewItem}
                </List>

                {filteredOptions.length === 0 && (
                    <DropdownMenuItem disabled={true}>
                        No option found.
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
